// src/pages/Insights/Tutorials/content/Python/PythonVirtualEnvs.js
import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonVirtualEnvs = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Step 2: The Workspace</div>
                <h1>Virtual Environments: Your Private Laboratory</h1>
                <p className="hero-lead">
                    In Python, keeping your work isolated isn't just a "best practice"it's a survival skill.
                    Learn how to prevent "Dependency Hell" and manage professional workspaces like a senior engineer.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <h2>The "Apartment Complex" Analogy</h2>
                <p>
                    To understand Virtual Environments, imagine your computer is a massive <strong>Apartment Complex</strong>.
                </p>
                <p>
                    The <strong>System Python</strong> is the <strong>Lobby</strong>. It is a shared space. If you decide to paint the Lobby walls neon pink (install a specific library version), everyone in the building has to look at it.
                    If your neighbor decides to tear up the carpet (uninstall a library), you trip over the bare concrete.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The Chaos of Global Installs:</strong> If you install everything in the Lobby (Global Python), eventually two tenants (Projects) will get into a fight.
                        Project A wants "Curtains v1.0" but Project B demands "Curtains v2.0". You cannot have both in the Lobby. This is <strong>Dependency Hell</strong>.
                    </div></ProTip>
                <p>
                    A <strong>Virtual Environment</strong> uses a better strategy: <strong>Private Apartments</strong>.
                    Each project gets its own locked room.
                    - In Room A (Project A), you install "Curtains v1.0".
                    - In Room B (Project B), you install "Curtains v2.0".
                    - The Lobby remains pristine and empty.
                </p>

                <div className="learning-objectives">
                    <h3>Engineering Goals</h3>
                    <ul>
                        <li><strong>Isolation:</strong> How to keep projects completely separate.</li>
                        <li><strong>Reproducibility:</strong> Sharing your "Apartment Blueprints" (`requirements.txt`) with others.</li>
                        <li><strong>Tooling:</strong> Choosing between <a href="https://docs.python.org/3/library/venv.html" target="_blank" rel="noopener noreferrer">venv</a>, <a href="https://python-poetry.org/" target="_blank" rel="noopener noreferrer">Poetry</a>, and <a href="https://docs.conda.io/en/latest/" target="_blank" rel="noopener noreferrer">Conda</a>.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>1. The Problem: Dependency Hell</h2>
                <p>
                    Let's look at a concrete technical example of why this matters.
                </p>
                <p>
                    Imagine you built a website in 2021 using <code>Django 3.2</code>. It works perfectly.
                    Today, you want to start a new project using <code>Django 5.0</code> (the latest version).
                </p>
                <p>
                    If you just type <code>pip install django</code> in your global terminal, pip will uninstall Django 3.2 and replace it with 5.0.
                    Suddenly, your old 2021 website crashes because the code is incompatible with the new version.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The Solution:</strong> You create two folders. In Folder A, you create a virtual environment with Django 3.2.
                        In Folder B, you create another with Django 5.0. They coexist on the same hard drive, completely unaware of each other.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Standard Tool: venv</h2>
                <p>
                    Python comes with a built-in module called <code>venv</code> (Virtual Environment). It is lightweight, standard, and comes with every Python installation since version 3.3.
                </p>

                <h3>Workflow: The "Create-Activate-Install" Loop</h3>
                <p>
                    This is the muscle memory of every Python developer. You will do this hundreds of times.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Step 1: Create the Environment</span>
                    </div>
                    <CodeBlock language="bash" code={`# Go to your project folder
cd my_project

# Create a virtual environment named ".venv"
# The "-m venv" flag tells Python to run the venv module
python -m venv .venv

# WHY ".venv"?
# The dot makes it hidden on Mac/Linux.
# It is a standard name that tools like VS Code recognize automatically.`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Step 2: Activate the Environment</span>
                    </div>
                    <CodeBlock language="bash" code={`# Windows (Command Prompt)
.venv\\Scripts\\activate.bat

# Windows (PowerShell)
.venv\\Scripts\\Activate.ps1

# macOS / Linux (Bash/Zsh)
source .venv/bin/activate

# SUCCESS INDICATOR:
# Your terminal prompt will change to show the env name:
# (.venv) user@computer:~/my_project $`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>What just happened?</strong> Activation temporarily changed your <strong>PATH</strong> variable.
                        It put your local `.venv/bin` folder at the very front of the list. Now, when you type `python` or `pip`,
                        the terminal finds the local version *before* the system version.
                    </div></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Step 3: Install Packages</span>
                    </div>
                    <CodeBlock language="bash" code={`# Now that you are activated (look for the .venv prefix)...

# Install a library (it goes into .venv/lib/site-packages)
pip install requests

# Check where it lives (Validation)
which python  # Mac/Linux
where python  # Windows
# Output should be: .../my_project/.venv/bin/python`} />
                </div>
            </div>

            <div className="content-section">
                <h2>3. Reproducibility: "It Works on My Machine"</h2>
                <p>
                    Great, your code works for you. But how do you send it to a friend or deploy it to a server? They don't have your `.venv` folder (and you should never send it to them!).
                </p>
                <p>
                    You need to send them the <strong>Blueprints</strong>. In Python, this is called <code>requirements.txt</code>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Freezing Dependencies</span>
                    </div>
                    <CodeBlock language="bash" code={`# Create the blueprint (Freeze current state)
pip freeze &gt; requirements.txt

# This creates a text file that looks like this:
# certifi==2024.2.2
# charset-normalizer==3.3.2
# idna==3.6
# requests==2.31.0
# urllib3==2.2.1`} />
                </div>

                <p>
                    Now, your friend downloads your code and the `requirements.txt` file. They create their <em>own</em> empty virtual environment, and then:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Installing from Blueprints</span>
                    </div>
                    <CodeBlock language="bash" code={`# Recreate the environment immediately
pip install -r requirements.txt

# Pip reads the list and installs the exact versions you used.
# Result: Perfect clone of your setup.`} />
                </div>
            </div>

            <div className="content-section">
                <h2>4. Tool Selection: venv vs Conda vs Poetry</h2>
                <p>
                    <code>venv</code> is great, but it's not the only game in town. Depending on your career path, you might use others.
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Tool</th>
                            <th>Best For</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>venv</strong></td>
                            <td>General Dev</td>
                            <td><strong>Standard Library.</strong> No installation needed. Lightweight. Perfect for web dev (Django/FastAPI) and scripts.</td>
                        </tr>
                        <tr>
                            <td><strong>Conda</strong></td>
                            <td>Data Science</td>
                            <td><strong>Heavyweight.</strong> Installs Python binary libraries (like NumPy) pre-compiled. Essential if doing AI/ML on Windows to avoid compiler errors.</td>
                        </tr>
                        <tr>
                            <td><strong>Poetry</strong></td>
                            <td>Production</td>
                            <td><strong>Modern Pro.</strong> Combines dependency management and packaging. Uses `pyproject.toml` instead of `requirements.txt`. Handles complex dependency trees better.</td>
                        </tr>
                    </tbody>
                </table>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Recommendation:</strong> Start with <code>venv</code>. It's built-in and solves 95% of problems.
                        Switch to <strong>Conda</strong> if you dive into Data Science. Switch to <strong>Poetry</strong> if you start building libraries for PyPI.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>5. Integration: VS Code Setup</h2>
                <p>
                    You might activate your environment in the terminal, but does VS Code know about it? If you see yellow squiggly lines under your imports, VS Code is likely looking at the wrong Python.
                </p>
                <div className="numbered-steps">
                    <div className="step">
                        <h4>1. Open your Project</h4>
                        <p>Open the folder containing your `.venv` in VS Code.</p>
                    </div>
                    <div className="step">
                        <h4>2. Open Command Palette</h4>
                        <p>Press <code>Ctrl+Shift+P</code> (Windows) or <code>Cmd+Shift+P</code> (Mac).</p>
                    </div>
                    <div className="step">
                        <h4>3. Select Interpreter</h4>
                        <p>Type "Python: Select Interpreter".</p>
                    </div>
                    <div className="step">
                        <h4>4. Pick the Right One</h4>
                        <p>Look for the entry that says `Python 3.x.x ('.venv': venv)`. It usually has a star  next to it.</p>
                    </div>
                </div>
                <p>
                    Once selected, VS Code uses that environment's libraries for auto-completion and debugging.
                </p>
            </div>

            <div className="content-section">
                <h2>6. Best Practices for Professionals</h2>
                <p>
                    Distinguish yourself from a beginner by following these unspoken rules of Python development.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Rule 1: Always Ignore.</strong> Before you write a single line of code, create a <code>.gitignore</code> file and add <code>.venv/</code> to it.
                        Committing 5,000 library files to GitHub is the hallmark of a novice.
                    </div></ProTip>

                <h3>Folder Structure</h3>
                <p>
                    Keep your environment <strong>inside</strong> your project folder. This makes it easy to delete the whole project in one go.
                </p>
                <CodeBlock language="text" code={`my_project/
 .venv/       # The Engine Room (Hidden, Ignored)
 src/         # Your Code
 tests/       # Your Tests
 .gitignore   # The Guard Dog
 requirements.txt # The Blueprint`} />
            </div>

            <div className="content-section">
                <h2>7. Troubleshooting: When Things Go Wrong</h2>
                <p>
                    Virtual environments are robust, but things can break.
                </p>

                <div className="troubleshooting-grid">
                    <div className="troubleshoot-item">
                        <h4> "Scripts is disabled on this system" (Windows)</h4>
                        <p><strong>Cause:</strong> PowerShell's default security policy blocks script execution.</p>
                        <p><strong>Fix:</strong> Open PowerShell as Administrator and run: <code>Set-ExecutionPolicy RemoteSigned</code>. This allows local scripts (like your activate script) to run.</p>
                    </div>

                    <div className="troubleshoot-item">
                        <h4> "Requirement already satisfied"</h4>
                        <p><strong>Cause:</strong> You forgot to activate! Pip is looking at the Global Python, seeing the package is there, and doing nothing.</p>
                        <p><strong>Fix:</strong> Check your prompt for `(.venv)`. If missing, activate immediately.</p>
                    </div>

                    <div className="troubleshoot-item">
                        <h4> "Command not found: activate"</h4>
                        <p><strong>Cause:</strong> You are trying to run `activate` as a command. It is a file path.</p>
                        <p><strong>Fix:</strong> You must <em>source</em> it. On Mac: `source .venv/bin/activate`. On Windows: `.venv\Scripts\activate`.</p>
                    </div>
                </div>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>1. One Project, One Env</h3>
                        <p>
                            Never share environments. Created a new folder? Create a new `.venv`.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>2. Commit the Code, Ignore the Env</h3>
                        <p>
                            Never upload the `.venv` folder to GitHub. It's huge and platform-specific. Add `.venv/` to your `.gitignore` file.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>3. Freeze Your State</h3>
                        <p>
                            Use `pip freeze &gt; requirements.txt` to save your environment state so others can reproduce it.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>4. Activate First</h3>
                        <p>
                            Every time you open a new terminal window to work, your first command should always be the activation command.
                        </p>
                    </div>
                </div>
            </div>

            <div className="external-resources">
                <h2> Further Reading</h2>
                <ul>
                    <li><a href="https://docs.python.org/3/library/venv.html" target="_blank" rel="noopener noreferrer">Official Python venv Documentation</a> - The definite guide.</li>
                    <li><a href="https://realpython.com/python-virtual-environments-a-primer/" target="_blank" rel="noopener noreferrer">RealPython: Virtual Environments Primer</a> - Excellent deep dive.</li>
                    <li><a href="https://python-poetry.org/docs/" target="_blank" rel="noopener noreferrer">Poetry Documentation</a> - For modern package management intro.</li>
                    <li><a href="https://docs.conda.io/projects/conda/en/latest/user-guide/concepts/environments.html" target="_blank" rel="noopener noreferrer">Conda Environments</a> - For Data Scientists.</li>
                </ul>
            </div>
        </div>
    );
};

export default PythonVirtualEnvs;


